
import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Button from '../../../components/base/Button';
import Input from '../../../components/base/Input';

export default function LoginForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState('');
  const [recoverySuccess, setRecoverySuccess] = useState(false);
  const { signIn, resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validaciones básicas
    if (!email || !password) {
      setError('Por favor complete todos los campos.');
      setLoading(false);
      return;
    }

    if (!email.includes('@')) {
      setError('Por favor ingrese un email válido.');
      setLoading(false);
      return;
    }

    const { error: signInError } = await signIn(email, password, rememberMe);
    
    if (signInError) {
      setError('Credenciales incorrectas. Verifique su email y contraseña.');
    }
    
    setLoading(false);
  };

  const handlePasswordRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setRecoveryLoading(true);
    setRecoveryMessage('');
    setRecoverySuccess(false);

    // Validación de email
    if (!recoveryEmail || !recoveryEmail.includes('@')) {
      setRecoveryMessage('Por favor ingrese un email válido.');
      setRecoveryLoading(false);
      return;
    }

    try {
      const result = await resetPassword(recoveryEmail);
      
      if (result.success) {
        setRecoverySuccess(true);
        setRecoveryMessage('Se ha enviado un email con las instrucciones para recuperar su contraseña. Revise su bandeja de entrada y carpeta de spam.');
        setRecoveryEmail('');
        
        // Volver al login después de 5 segundos
        setTimeout(() => {
          setShowRecovery(false);
          setRecoveryMessage('');
          setRecoverySuccess(false);
        }, 5000);
      } else {
        setRecoveryMessage(result.error || 'Error al procesar la solicitud. Verifique que el email esté registrado como administrador.');
      }
    } catch (error) {
      setRecoveryMessage('Error de conexión. Intente nuevamente.');
    }

    setRecoveryLoading(false);
  };

  const resetForm = () => {
    setShowRecovery(false);
    setRecoveryMessage('');
    setRecoverySuccess(false);
    setRecoveryEmail('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-100">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-2xl border border-gray-100">
        <div className="text-center">
          <div className="mb-6 flex flex-col items-center">
            <div className="w-20 h-20 bg-teal-600 rounded-full flex items-center justify-center mb-4">
              <i className="ri-admin-line text-3xl text-white"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 font-pacifico">Grupo FR Venture</h1>
            <div className="w-16 h-1 bg-teal-600 mx-auto rounded-full mt-2"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Panel de Administración</h2>
          <p className="mt-2 text-gray-600">Acceso exclusivo para administradores</p>
        </div>
        
        {!showRecovery ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email de Administrador"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                icon="ri-admin-line"
                placeholder="admin@grupofrventure.com"
                autoComplete="email"
              />
              
              <Input
                label="Contraseña"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                icon="ri-lock-line"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            {/* Checkbox Recordar Sesión */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer">
                  Recordar mi sesión
                </label>
              </div>
              
              <button
                type="button"
                onClick={() => setShowRecovery(true)}
                className="text-sm text-teal-600 hover:text-teal-700 transition-colors cursor-pointer font-medium"
              >
                ¿Olvidó su contraseña?
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-500 mr-3 text-lg"></i>
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            <Button
              type="submit"
              loading={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 focus:ring-teal-500"
              size="lg"
            >
              <i className="ri-login-circle-line mr-2"></i>
              {loading ? 'Iniciando Sesión...' : 'Iniciar Sesión'}
            </Button>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                <i className="ri-shield-check-line mr-1"></i>
                Acceso restringido solo para personal autorizado
              </p>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordRecovery}>
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-send-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900">Recuperar Contraseña</h3>
              <p className="text-gray-600 text-sm mt-2">
                Ingrese su email registrado para recibir instrucciones de recuperación
              </p>
            </div>

            <Input
              label="Email de Administrador"
              type="email"
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              required
              icon="ri-mail-line"
              placeholder="admin@grupofrventure.com"
              autoComplete="email"
            />

            {recoveryMessage && (
              <div className={`border rounded-lg p-4 ${
                recoverySuccess
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start">
                  <i className={`${
                    recoverySuccess
                      ? 'ri-check-circle-line text-green-500' 
                      : 'ri-error-warning-line text-red-500'
                  } mr-3 text-lg mt-0.5`}></i>
                  <div>
                    <p className={`text-sm font-medium ${
                      recoverySuccess ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {recoveryMessage}
                    </p>
                    {recoverySuccess && (
                      <p className="text-xs text-green-600 mt-1">
                        Regresando al login automáticamente...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex space-x-3">
              <Button
                type="button"
                onClick={resetForm}
                variant="outline"
                className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Volver al Login
              </Button>
              
              <Button
                type="submit"
                loading={recoveryLoading}
                disabled={recoverySuccess}
                className="flex-1 bg-blue-600 hover:bg-blue-700 focus:ring-blue-500"
              >
                <i className="ri-send-plane-line mr-2"></i>
                {recoveryLoading ? 'Enviando...' : 'Enviar Instrucciones'}
              </Button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                <i className="ri-information-line mr-1"></i>
                Si no recibe el email, revise su carpeta de spam
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
